import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { ConfigurationTableSection } from '../_shared/ConfigurationTable';
import { copy } from '../_shared/copy';
import { EditDialog } from '../_shared/EditDialog';
import { errors } from '../_shared/errors';
import { useRotateConfiguration, useUpdateConfiguration } from '../_shared/mutations';
import { RotateDialog } from '../_shared/RotateDialog';
import { serviceSectionFor } from '../_shared/services';
import { SettingsStrip } from '../_shared/SettingsStrip';
import type { ConfigurationRow } from '../_shared/types';
import { useAdministeredSections } from '../_shared/useAdministeredSections';

interface Draft {
  value?: string;
  description: string;
}

/**
 * `/settings/<service>` — one service's runtime configuration.
 *
 * **This surface reads and edits. It does not add a key, remove one, or rename
 * one** — all three live in the owning service's code, and the runtime grant on
 * every one of those tables is exactly `SELECT, UPDATE (value, description)`, so
 * the API could not do any of the three even if a screen asked.
 *
 * **The editing dialog does not write; the confirmation does.** Pressing the
 * edit dialog's commit closes it and opens a second one naming the key and
 * stating the change, and only that dialog's accept touches the row. A **no-op
 * never reaches the confirmation**: re-typing the same trimmed string closes the
 * dialog and nothing more, because a confirmation for a write that will not
 * happen trains an operator to dismiss confirmations.
 *
 * **One service, one read.** `/settings` used to draw every service at once —
 * eight sections, 240 rows and 23 653 px of page on develop, behind eight
 * parallel reads fired on arrival, for an operator who came to change one key.
 * The fan-out is gone with the page that fired it, and the per-service error
 * boundary that used to justify it is now simply this page's own state.
 *
 * A segment naming a service this account does not administer, or no service at
 * all, renders a named refusal with the way back — never a blank page and never
 * a redirect, which would take the address the operator typed away from them
 * before they could read what was wrong with it.
 *
 * What the surface deliberately does not carry: **no search or filter over the
 * keys** (none is named, and the keys are now reachable by opening one labelled
 * service), **no per-key history, previous-value view, or undo** (all three are
 * the deferred audit log wearing a different name — which is also why the
 * confirmation copy says the confirm step is the only guard that ships), **no
 * environment indicator**, and **no test or validate action** on a value: the
 * owning service validates before the write and refuses what it cannot parse,
 * and a second validator on this side that disagreed with it would be worse than
 * none.
 */
export function SettingsServicePage() {
  const toast = useToast();
  const { service: slug } = useParams();

  const sections = useAdministeredSections();
  const spec = serviceSectionFor(slug);
  // Administering the service is what makes the page openable — a segment naming
  // a real service this account does not hold `admin` in is refused here rather
  // than by the service, so the answer arrives without a request.
  const section = spec && sections.some((entry) => entry.key === spec.key) ? spec : undefined;

  const updateConfiguration = useUpdateConfiguration();
  const rotateConfiguration = useRotateConfiguration();

  const [editing, setEditing] = useState<ConfigurationRow | null>(null);
  const [rotating, setRotating] = useState<ConfigurationRow | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [pendingWrite, setPendingWrite] = useState<{ row: ConfigurationRow; draft: Draft } | null>(null);
  // Held only across a refused write, so the reopened dialog carries what the
  // operator typed rather than the row they were trying to replace.
  const [refusedDraft, setRefusedDraft] = useState<Draft | null>(null);

  // Walking to a sibling service through the strip must not leave a dialog
  // standing over a key belonging to the table the operator just left.
  useEffect(() => {
    setEditing(null);
    setRotating(null);
    setRotateError(null);
    setConfirmSubject(null);
    setPendingWrite(null);
    setRefusedDraft(null);
  }, [slug]);

  function failureMessage(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    return resolveErrorCopy(code, status, errors).message;
  }

  /** The edit dialog hands its draft here; this is where the confirmation opens. */
  function reviewDraft(draft: Draft) {
    const row = editing;
    if (!row) return;

    setRefusedDraft(null);

    const valueChanged = draft.value !== undefined && draft.value.trim() !== (row.value ?? '').trim();
    const descriptionChanged = draft.description.trim() !== row.description.trim();

    setEditing(null);

    // A no-op never reaches the confirmation, and never reaches the wire.
    if (!valueChanged && !descriptionChanged) {
      toast.show(copy.toast.unchanged, 'info');
      return;
    }

    setPendingWrite({ row, draft });
    setConfirmSubject({
      heading: valueChanged ? copy.confirm.heading(row.key) : copy.confirm.headingDescriptionOnly(row.key),
      body: copy.confirm.body,
      commitLabel: copy.confirm.commit,
      // Reversible-but-consequential: the value takes effect on the service, and
      // nothing here destroys a record.
      intent: 'warning',
    });
  }

  function closeConfirm() {
    setConfirmSubject(null);
    setPendingWrite(null);
  }

  function commitWrite() {
    const pending = pendingWrite;
    if (!pending || !section) return;

    const { row, draft } = pending;
    const nextValue = draft.value;
    const valueChanged = nextValue !== undefined && nextValue.trim() !== (row.value ?? '').trim();

    // A secret's value is never sent on this route — the service refuses it, and
    // rotation is the only path that carries one. The key is **omitted** rather
    // than sent as `undefined`, which is the same distinction the wire draws
    // between "leave the description alone" and "write an empty description".
    const body =
      nextValue !== undefined && valueChanged
        ? { value: nextValue, description: draft.description }
        : { description: draft.description };

    updateConfiguration.mutate(
      { service: section.key, configurationKey: row.key, body },
      {
        onSuccess: () => {
          closeConfirm();
          toast.show(valueChanged ? copy.toast.written(row.key) : copy.toast.described(row.key), 'success');
        },
        // The failure toast asks the operator to review the value, and there is
        // nothing left to review if the typed string went with the dialog. The
        // confirmation closes — its question was answered — and the edit dialog
        // reopens holding the refused draft, so the operator corrects one
        // character instead of retyping the value from the row they replaced.
        onError: (error) => {
          setConfirmSubject(null);
          setPendingWrite(null);
          setRefusedDraft(draft);
          setEditing(row);
          toast.show(failureMessage(error), 'danger');
        },
      },
    );
  }

  function commitRotation(value: string) {
    const row = rotating;
    if (!row || !section) return;

    setRotateError(null);
    rotateConfiguration.mutate(
      { service: section.key, configurationKey: row.key, value },
      {
        onSuccess: () => {
          setRotating(null);
          toast.show(copy.toast.rotated(row.key), 'success');
        },
        onError: (error) => setRotateError(failureMessage(error)),
      },
    );
  }

  if (!section) {
    return (
      <>
        <Breadcrumb
          items={[
            { label: copy.crumbs.console, to: '/' },
            { label: copy.crumb, to: '/settings' },
            { label: copy.service.unknownTitle },
          ]}
        />

        <div className="mb-6 mt-3">
          <h1 className="font-display text-h1 font-bold">{copy.service.unknownTitle}</h1>
        </div>

        <Alert kind="warning" title={copy.service.unknownTitle}>
          {copy.service.unknownBody}
        </Alert>

        <p className="mt-6">
          <Link to="/settings" className="text-accent-text">
            {copy.service.back}
          </Link>
        </p>
      </>
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: copy.crumbs.console, to: '/' },
          { label: copy.crumb, to: '/settings' },
          { label: section.heading },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{section.heading}</h1>
      </div>

      <SettingsStrip sections={sections} current={section.key} />

      <ConfigurationTableSection
        spec={section}
        onEdit={(row) => {
          setRefusedDraft(null);
          setEditing(row);
        }}
        onRotate={(row) => {
          setRotateError(null);
          setRotating(row);
        }}
      />

      <EditDialog
        open={editing !== null}
        row={editing}
        serviceTable={section.table}
        seed={refusedDraft}
        onReview={reviewDraft}
        onCancel={() => {
          setRefusedDraft(null);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={updateConfiguration.isPending}
        onConfirm={commitWrite}
        onCancel={closeConfirm}
      >
        {/* The change itself, stated before the press. */}
        {pendingWrite && pendingWrite.draft.value !== undefined ? (
          <p className="mb-0 max-w-none text-xs text-ink-muted">
            {copy.confirm.from} <bdi className="mono">{pendingWrite.row.value}</bdi> {copy.confirm.to}{' '}
            <bdi className="mono">{pendingWrite.draft.value}</bdi>
          </p>
        ) : null}
      </ConfirmDialog>

      <RotateDialog
        open={rotating !== null}
        configurationKey={rotating?.key ?? null}
        busy={rotateConfiguration.isPending}
        formError={rotateError}
        onCommit={commitRotation}
        onCancel={() => setRotating(null)}
      />
    </>
  );
}
